import { useEffect, useMemo, useState } from 'react'
import { Plus, Save, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/common/Badge'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { Modal } from '@/components/common/Modal'
import ContactsService from '@/services/contacts.service'
import ConversationAIService from '@/services/conversation-ai.service'
import type { Contact, ContactCustomField, Conversation, ConversationAISetting } from '@/types'

interface ContactDetailsModalProps {
  open: boolean
  conversation: Conversation | null
  instanceName?: string
  onClose: () => void
  onSaved?: (contact: Contact, aiSetting: ConversationAISetting) => void
}

interface EditableCustomField {
  id: string
  field_key: string
  field_label: string
  field_type: string
  field_value: string
  persisted: boolean
}

const FIELD_TYPES = ['text', 'number', 'date', 'boolean', 'select', 'email', 'phone', 'url'] as const

function toEditableField(field: ContactCustomField): EditableCustomField {
  return {
    id: field.id,
    field_key: field.field_key,
    field_label: field.field_label,
    field_type: field.field_type,
    field_value: field.field_value,
    persisted: true,
  }
}

function createEmptyField(): EditableCustomField {
  return {
    id: `draft-${Math.random().toString(36).slice(2, 10)}`,
    field_key: '',
    field_label: '',
    field_type: 'text',
    field_value: '',
    persisted: false,
  }
}

export function ContactDetailsModal({
  open,
  conversation,
  instanceName,
  onClose,
  onSaved,
}: ContactDetailsModalProps) {
  const [contact, setContact] = useState<Contact | null>(conversation?.contact ?? null)
  const [contactForm, setContactForm] = useState({
    name: '',
    phone_number: '',
    avatar_url: '',
    blocked: false,
  })
  const [customFields, setCustomFields] = useState<EditableCustomField[]>([])
  const [removedFieldIds, setRemovedFieldIds] = useState<string[]>([])
  const [aiSetting, setAiSetting] = useState<ConversationAISetting | null>(null)
  const [disabledReason, setDisabledReason] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const labels = useMemo(() => contact?.labels ?? [], [contact?.labels])

  useEffect(() => {
    if (!open || !conversation) return
    const currentConversation = conversation

    let cancelled = false

    async function loadDetails() {
      setIsLoading(true)
      try {
        const [loadedContact, loadedFields, loadedAiSetting] = await Promise.all([
          ContactsService.getById(currentConversation.contact_id),
          ContactsService.getCustomFields(currentConversation.contact_id),
          ConversationAIService.get(currentConversation.id),
        ])

        if (cancelled) return

        setContact(loadedContact)
        setContactForm({
          name: loadedContact.name ?? '',
          phone_number: loadedContact.phone_number ?? '',
          avatar_url: loadedContact.avatar_url ?? '',
          blocked: Boolean(loadedContact.blocked),
        })
        setCustomFields(loadedFields.map(toEditableField))
        setRemovedFieldIds([])
        setAiSetting(loadedAiSetting)
        setDisabledReason(loadedAiSetting.disabled_reason ?? '')
      } catch {
        if (!cancelled) {
          toast.error('Não foi possível carregar os detalhes do contato.')
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    loadDetails().catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [conversation, open])

  function updateField(id: string, updates: Partial<EditableCustomField>) {
    setCustomFields((current) => current.map((field) => (field.id === id ? { ...field, ...updates } : field)))
  }

  function removeField(field: EditableCustomField) {
    setCustomFields((current) => current.filter((item) => item.id !== field.id))
    if (field.persisted) {
      setRemovedFieldIds((current) => [...current, field.id])
    }
  }

  async function handleSave() {
    if (!conversation || !contact) return

    setIsSaving(true)
    try {
      const updatedContact = await ContactsService.update(contact.id, {
        name: contactForm.name.trim() || undefined,
        phone_number: contactForm.phone_number.trim() || undefined,
        avatar_url: contactForm.avatar_url.trim() || undefined,
        blocked: contactForm.blocked,
      })

      for (const fieldId of removedFieldIds) {
        await ContactsService.deleteCustomField(updatedContact.id, fieldId)
      }

      const savedFields: ContactCustomField[] = []
      for (const field of customFields) {
        if (!field.field_key.trim() || !field.field_label.trim()) {
          continue
        }
        const payload = {
          field_key: field.field_key.trim(),
          field_label: field.field_label.trim(),
          field_type: field.field_type,
          field_value: field.field_value,
        }
        if (field.persisted) {
          savedFields.push(await ContactsService.updateCustomField(updatedContact.id, field.id, payload))
        } else {
          savedFields.push(await ContactsService.createCustomField(updatedContact.id, payload))
        }
      }

      const updatedAiSetting = await ConversationAIService.update(conversation.id, {
        ai_enabled: Boolean(aiSetting?.ai_enabled),
        disabled_reason: aiSetting?.ai_enabled ? null : disabledReason.trim() || null,
      })

      const finalContact: Contact = {
        ...updatedContact,
        custom_fields_list: savedFields,
      }

      setContact(finalContact)
      setCustomFields(savedFields.map(toEditableField))
      setRemovedFieldIds([])
      setAiSetting(updatedAiSetting)
      onSaved?.(finalContact, updatedAiSetting)
      toast.success('Detalhes do contato atualizados.')
      onClose()
    } catch {
      toast.error('Não foi possível salvar os detalhes do contato.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={contact?.name || contact?.phone_number || 'Detalhes do contato'}
      description="Edite os dados do lead, gerencie campos personalizados e controle a IA por conversa."
      size="xl"
    >
      {!conversation || !contact ? null : isLoading ? (
        <div className="py-10 text-sm text-[var(--text-muted)]">Carregando detalhes do contato...</div>
      ) : (
        <div className="space-y-6">
          <section className="grid gap-4 md:grid-cols-2">
            <Input
              label="Nome"
              value={contactForm.name}
              onChange={(event) =>
                setContactForm((current) => ({ ...current, name: event.target.value }))
              }
            />
            <Input
              label="Telefone"
              value={contactForm.phone_number}
              onChange={(event) =>
                setContactForm((current) => ({ ...current, phone_number: event.target.value }))
              }
            />
            <Input label="JID" value={contact.jid} readOnly />
            <Input label="Instância" value={instanceName ?? conversation.instance_id} readOnly />
            <Input
              label="Foto"
              value={contactForm.avatar_url}
              onChange={(event) =>
                setContactForm((current) => ({ ...current, avatar_url: event.target.value }))
              }
            />
            <Input
              label="Última interação"
              value={contact.last_message_at ? new Date(contact.last_message_at).toLocaleString() : 'Sem histórico'}
              readOnly
            />
          </section>

          <section className="rounded-[18px] border border-[var(--border-subtle)] bg-[var(--glass-light)] p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">IA ativa nesta conversa</p>
                <p className="mt-1 text-xs text-[var(--text-muted)]">
                  Quando desligada, o backend não dispara LangGraph nem resposta automática.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(aiSetting?.ai_enabled)}
                onClick={() =>
                  setAiSetting((current) =>
                    current
                      ? { ...current, ai_enabled: !current.ai_enabled }
                      : current,
                  )
                }
                className={`relative inline-flex h-8 w-14 items-center rounded-full border transition ${
                  aiSetting?.ai_enabled
                    ? 'border-[rgba(16,185,129,0.28)] bg-[rgba(16,185,129,0.22)]'
                    : 'border-[var(--border-subtle)] bg-[rgba(255,255,255,0.08)]'
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 rounded-full bg-white shadow transition ${
                    aiSetting?.ai_enabled ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            {!aiSetting?.ai_enabled && (
              <div className="mt-4">
                <Input
                  label="Motivo da desativação"
                  value={disabledReason}
                  onChange={(event) => setDisabledReason(event.target.value)}
                  placeholder="Ex.: atendimento manual em andamento"
                />
              </div>
            )}
          </section>

          <section className="grid gap-4 md:grid-cols-2">
            <div className="rounded-[18px] border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.03)] p-4">
              <p className="text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">Status</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge variant={contact.blocked ? 'error' : 'success'}>
                  {contact.blocked ? 'Bloqueado' : 'Ativo'}
                </Badge>
                {contact.avatar_status ? <Badge variant="default">{contact.avatar_status}</Badge> : null}
              </div>
              <label className="mt-4 flex items-center gap-2 text-sm text-[var(--text-primary)]">
                <input
                  type="checkbox"
                  checked={contactForm.blocked}
                  onChange={(event) =>
                    setContactForm((current) => ({ ...current, blocked: event.target.checked }))
                  }
                />
                Contato bloqueado
              </label>
            </div>

            <div className="rounded-[18px] border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.03)] p-4">
              <p className="text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">Tags</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {labels.length > 0 ? labels.map((label) => <Badge key={label} variant="brand">{label}</Badge>) : (
                  <span className="text-sm text-[var(--text-muted)]">Sem tags vinculadas.</span>
                )}
              </div>
            </div>
          </section>

          <section className="space-y-4 rounded-[18px] border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.03)] p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">Campos personalizados</p>
                <p className="mt-1 text-xs text-[var(--text-muted)]">
                  Cada campo fica persistido no contato e reaparece após reload.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                icon={<Plus size={14} />}
                onClick={() => setCustomFields((current) => [...current, createEmptyField()])}
              >
                Novo campo
              </Button>
            </div>

            {customFields.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)]">Nenhum campo personalizado cadastrado.</p>
            ) : (
              <div className="space-y-3">
                {customFields.map((field) => (
                  <div
                    key={field.id}
                    className="grid gap-3 rounded-[16px] border border-[var(--border-subtle)] bg-[var(--glass-light)] p-3 md:grid-cols-[1fr,1fr,180px,1fr,44px]"
                  >
                    <Input
                      label="Label"
                      value={field.field_label}
                      onChange={(event) => updateField(field.id, { field_label: event.target.value })}
                    />
                    <Input
                      label="Chave"
                      value={field.field_key}
                      onChange={(event) => updateField(field.id, { field_key: event.target.value })}
                    />
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-[var(--text-primary)]">Tipo</label>
                      <select
                        value={field.field_type}
                        onChange={(event) => updateField(field.id, { field_type: event.target.value })}
                        className="rounded-[var(--radius-md)] border border-[var(--border-medium)] bg-[var(--glass-light)] px-4 py-2.5 text-sm text-[var(--text-primary)]"
                      >
                        {FIELD_TYPES.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <Input
                      label="Valor"
                      value={field.field_value}
                      onChange={(event) => updateField(field.id, { field_value: event.target.value })}
                    />
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => removeField(field)}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-[14px] border border-[rgba(239,68,68,0.16)] bg-[rgba(239,68,68,0.08)] text-[var(--color-error)] transition hover:bg-[rgba(239,68,68,0.12)]"
                        aria-label="Remover campo"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button icon={<Save size={14} />} loading={isSaving} onClick={() => handleSave().catch(() => undefined)}>
              Salvar alterações
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
